import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Row } from 'antd'
import cx from 'classnames'
import get from 'lodash/get'
import noop from 'lodash/noop'
import omit from 'lodash/omit'
import template from 'lodash/template'
import Head from 'next/head'
import Script from 'next/script'
import PropTypes from 'prop-types'
import { CSSTransition } from 'react-transition-group'

import Services from './Services'
import Amenity from './widgets/Amenity'
import Cart from './widgets/Cart'
import Map from './widgets/Map'
import SchoolDescription from './widgets/SchoolDescription'
import SchoolHero from './widgets/SchoolHero'
import SchoolHighlights from './widgets/SchoolHighlights'
import SchoolNationality from './widgets/SchoolNationality'
import { interpolate, useTranslations } from 'common/language'
import { lg } from 'common/utils/breakpoints'
import { getOgImage } from 'common/utils/getOgImage'
import parseTemplateData from 'common/utils/parseTemplateData'
import scrollSmoothTo from 'common/utils/scrollSmoothTo'
import structuredDataSchema from 'common/utils/structuredDataSchema'
import { BlockSectionTitle, Button, Container } from 'common/widgets'
import { ExperienceMasonry, ProductReviews } from 'common/widgets/blocks'
import useCart from 'hooks/useCart'
import classNames from './styles.module.scss'


const cartIcon = ['fal', 'shopping-cart']

SchoolPageView.propTypes = {
  schoolNew: PropTypes.object,
  openSidebar: PropTypes.bool,
  toggleSidebar: PropTypes.func,
  seoTemplates: PropTypes.object.isRequired,
}

export default function SchoolPageView({
  schoolNew = {},
  toggleSidebar = noop,
  openSidebar = false,
  seoTemplates,
}) {
  const cartData = useCart()
  const cartLength = useMemo(() => Object.keys(cartData.data).length, [cartData.data])
  const [isMounted, setMounted] = useState(false)
  const { gettext, languageCode } = useTranslations()
  const seoContext = useMemo(() => ({
    'country_name': get(schoolNew, 'static_content.country.name', ''),
    'destination_name': get(schoolNew, 'static_content.destination.name', ''),
    'school_name': get(schoolNew, 'name', ''),
  }), [schoolNew])
  const title = template(parseTemplateData(get(seoTemplates['public_site.SchoolPage'], `title_${languageCode}`, ''), seoContext))(seoContext)
  const description = template(parseTemplateData(get(seoTemplates['public_site.SchoolPage'], `description_${languageCode}`, ''), seoContext))(seoContext)
  const origin = useMemo(() => typeof window === 'undefined' ? process.env.ORIGIN_URL : window.location.origin, [])
  const generateLinkPath = useCallback((data, lang) => {
    return origin + `/${lang}/${data.country_slug}/${data.destination_slug}/${data.school_slug}`
  }, [origin])
  const alternateDe = useMemo(() => get(schoolNew, 'static_content.hreflang_data.de'), [schoolNew?.static_content?.hreflang_data])
  const alternateFr = useMemo(() => get(schoolNew, 'static_content.hreflang_data.fr'), [schoolNew?.static_content?.hreflang_data])
  const isSchemaEnabled = useMemo(() => get(schoolNew, 'static_content.rating.reviews_count', 0) > 0, [schoolNew?.static_content?.rating?.reviews_count])
  const minPrice = get(schoolNew, 'min_price', null)
  const lowestCoursePrice = minPrice?.currency === 'CHF' ? minPrice?.value : get(minPrice, 'reference.value', null)
  const ogImage = useMemo(() => {
    const image = get(schoolNew, 'static_content.media_images[0]', {})
    const imageWithData = {
      bynder: image.bynder,
      data: {
        name: image.data?.[`name_${languageCode}`] ?? '',
      },
    }
    return getOgImage({
      value: {
        image: imageWithData,
      },
    })
  }, [schoolNew])
  const schema = useMemo(() => isSchemaEnabled && structuredDataSchema({
    url: generateLinkPath(get(schoolNew, ['static_content', 'hreflang_data', languageCode], ''), languageCode),
    name: interpolate(gettext('Language Stay %s'), [get(schoolNew, 'name', '')]),
    score: get(schoolNew, 'static_content.rating.overall', ''),
    reviewCount: get(schoolNew, 'static_content.rating.reviews_count', ''),
    image: ogImage,
    offers: lowestCoursePrice ? { lowPrice: lowestCoursePrice, priceCurrency: 'CHF' } : null,
  }), [isSchemaEnabled, schoolNew.static_content.hreflang_data, schoolNew.static_content.rating, ogImage, lowestCoursePrice])

  useEffect(() => {
    setMounted(true)
  }, [])

  const onCartSideNavClick = useCallback(() => {
    if(typeof window !== 'undefined' && window.innerWidth <= lg) {
      toggleSidebar()
    } else {
      scrollSmoothTo('prices')
    }
  }, [toggleSidebar])

  return (
    <Fragment>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} key="description" />
        {schoolNew?.static_content?.hreflang_data && (
          <link rel="canonical" href={generateLinkPath(schoolNew?.static_content?.hreflang_data[languageCode], languageCode)} />
        )}
        {alternateDe && <link rel="alternate" hrefLang="de" href={generateLinkPath(alternateDe, 'de')} />}
        {alternateFr && <link rel="alternate" hrefLang="fr" href={generateLinkPath(alternateFr, 'fr')} />}
        {alternateDe && <link rel="alternate" hrefLang="x-default" href={generateLinkPath(alternateDe, 'de')} />}
        <meta property="og:title" content={title} key="og-title" />
        <meta property="og:description" content={description} key="og-description" />
        <meta property="og:image" content={ogImage} key="og-image" />
        <meta property="og:url" content={generateLinkPath(schoolNew?.static_content?.hreflang_data[languageCode], languageCode)} key="og-url" />
        <meta property="og:type" content="website" key="og-type" />
      </Head>
      {isSchemaEnabled && (
        // eslint-disable-next-line @next/next/no-before-interactive-script-outside-document
        <Script
          id={'schema_school_' + get(schoolNew, 'slug', '')}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: schema }}
          strategy="beforeInteractive"
        />
      )}
      <SchoolHero languageCode={languageCode} />
      <SchoolDescription />
      <Amenity />
      <Container className={classNames.HighWrapper} size="xl">
        <Row>
          <Col
            span={24}
            lg={13}
          >
            <SchoolHighlights />
          </Col>
          <Col span={0} lg={1} />
          <Col
            span={24}
            lg={9}
          >
            <SchoolNationality />
          </Col>
        </Row>
      </Container>
      <Container size="xl">
        <Map />
      </Container>
      <Container size="xl" className={classNames.servicesTitleWrapper}>
        <BlockSectionTitle
          heading={`${schoolNew.name || ''}${gettext(' - Courses & Accommodation')}`}
        />
      </Container>
      <div className={classNames.CartWrapper} id="prices">
        <Container className={classNames.CartContainer} size="xl">
          <Row gutter={{ xs: 0, lg: 20 }}>
            <CSSTransition
              in={openSidebar}
              timeout={500}
              classNames="transition-cart"
            >
              <Col
                span={0}
                lg={7}
                className={cx(classNames.cart)}
              >
                <Cart toggleCart={toggleSidebar} withSchoolMock withLink />
              </Col>
            </CSSTransition>
            <Col
              span={24}
              lg={17}
            >
              <Services />
            </Col>
          </Row>
        </Container>
        <div className={classNames.triggerWrapper}>
          <Button
            icon={cartIcon}
            iconBadge={isMounted && cartLength > 0 ? cartLength : undefined}
            className={cx(classNames.sideNav)}
            onClick={onCartSideNavClick}
          >
            <span className={classNames.buttonText}>
              {gettext('Prices')}
            </span>
          </Button>
        </div>
      </div>
      <Container size="xl">
        <BlockSectionTitle
          heading={interpolate(gettext('%s Customer Reviews'), [schoolNew.name])}
          id="reviews"
        />
      </Container>
      <ProductReviews
        data={{
          kind: 'school',
          parent_id: schoolNew?.id,
          rating: schoolNew.static_content.rating.overall,
          reviews_count: schoolNew.static_content.rating.reviews_count,
          scores: omit(schoolNew.static_content.rating, ['reviews_count', 'overall']),
        }}
      />
      <Container size="xl">
        <BlockSectionTitle
          className={classNames.snapshotsTitle}
          heading={interpolate(gettext('%s Snapshots'), [schoolNew.name])}
        />
      </Container>
      <ExperienceMasonry
        data={{ kind: 'school', parent_id: schoolNew?.id }}
        withoutCTA
      />
    </Fragment>
  )
}
